import React, { useState, useRef, useEffect } from 'react';
import { 
    School, Network, ChevronDown, 
    Package, AlertTriangle, XCircle, DollarSign, 
    Search, Filter, MoreHorizontal, ChevronRight,
    Loader2, ChevronLeft, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import Header from "../components/Header";
import api from '../connector/api';
import { Link } from 'react-router-dom';

const menuItems = [
    {
      name: "Rooms Management",
      icon: <School size={15} />,
      children: [
        { name: "Room Equipment", path: "/inventory/room-equipment" },
      ],
    },
    {
      name: "IT Infrastructure",
      icon: <Network size={15} />,
      children: [
        { name: "CCTV Camera", path: "/inventory/cctv-camera" },
        { name: "Desktop Computer", path: "/inventory/desktop-computer" },
        { name: "Media Equipment", path: "/inventory/media-equipment" },
        { name: "Network Equipment", path: "/inventory/network-equipment" },
      ],
    },
];

export default function InventoryControl() {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeDropdown, setActiveDropdown] = useState(null); 
    const navRef = useRef(null);

    // Live State Streams
    const [inventoryItems, setInventoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState({
        totalSKUs: 0,
        lowStock: 0,
        outOfStock: 0,
        valuation: 0
    });
    const [criticalTriggers, setCriticalTriggers] = useState([]);

    // PAGINATION STATE
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Reset pagination window back to page 1 whenever search filters mutate
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Auto-close contextual dropdown panels if clicking external whitespace
    useEffect(() => {
        function handleClickOutside(event) {
            if (navRef.current && !navRef.current.contains(event.target)) {
                setActiveDropdown(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch live data records from Django viewsets
    useEffect(() => {
        async function fetchMasterInventory() {
            setLoading(true);
            try {
                const [roomEquipmentRes, itEquipmentRes] = await Promise.all([
                    api.get('/equipment/').catch(() => ({ data: [] })),
                    api.get('/it-equipment/').catch(() => ({ data: [] }))
                ]);

                const roomData = Array.isArray(roomEquipmentRes.data) 
                    ? roomEquipmentRes.data 
                    : (roomEquipmentRes.data?.results || []);
                const itData = Array.isArray(itEquipmentRes.data) 
                    ? itEquipmentRes.data 
                    : (itEquipmentRes.data?.results || []);

                // Normalize RoomEquipment schema
                const mappedRoomItems = roomData.map((item, idx) => {
                    const quantity = parseInt(item.quantity || 0, 10);
                    return {
                        id: `room-${item.id || idx}`,
                        sku: `RE-${String(item.id || idx).padStart(4, '0')}`,
                        name: item.name || "Unnamed Asset",
                        category: "Room Equipment",
                        stock: quantity,
                        cost: 0.00, 
                        location: item.room?.code || "Unassigned",
                        meta: "General Room Asset",
                        status: quantity === 0 ? "Out of Stock" : (quantity < 3 ? "Low Stock" : "In Stock")
                    };
                });

                // Normalize ITEquipment schema
                const mappedItItems = itData.map((item, idx) => {
                    const costVal = parseFloat(item.cost || 0);
                    const displayName = item.description || item.model || `${item.category || 'IT'} Asset`;

                    return {
                        id: `it-${item.id || idx}`,
                        sku: item.serial_number ? String(item.serial_number).toUpperCase() : `IT-${String(item.id || idx).padStart(4, '0')}`,
                        name: displayName,
                        category: item.category || "Other",
                        stock: 1, 
                        cost: costVal,
                        location: item.location || "Central Storage",
                        meta: item.responsible_person_name ? `Caretaker: ${item.responsible_person_name}` : "No Caretaker Assigned",
                        status: "In Stock" 
                    };
                });

                const combinedRegistry = [...mappedRoomItems, ...mappedItItems];
                setInventoryItems(combinedRegistry);

                // Dashboard summary updates
                const totalSKUs = combinedRegistry.length;
                const outOfStock = combinedRegistry.filter(i => i.stock === 0).length;
                const lowStock = combinedRegistry.filter(i => i.status === "Low Stock").length;
                const valuation = mappedItItems.reduce((acc, curr) => acc + curr.cost, 0);

                setMetrics({ totalSKUs, lowStock, outOfStock, valuation });

                const triggers = combinedRegistry
                    .filter(i => i.stock === 0 || i.status === "Low Stock")
                    .map(i => ({
                        name: i.name,
                        qty: i.stock,
                        min: 3,
                        status: i.stock === 0 ? "Out of Stock" : "Critical"
                    }));
                setCriticalTriggers(triggers);

            } catch (error) {
                console.error("Master inventory evaluation crashed:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchMasterInventory();
    }, []);

    // 1. First, process search matching strings
    const baseFilteredItems = inventoryItems.filter(item => {
        const query = searchTerm.toLowerCase();
        return (
            (item.sku || '').toLowerCase().includes(query) ||
            (item.name || '').toLowerCase().includes(query) ||
            (item.category || '').toLowerCase().includes(query) ||
            (item.location || '').toLowerCase().includes(query) ||
            (item.meta || '').toLowerCase().includes(query)
        );
    });

    // 2. Next, isolate strictly IT Equipment entries
    const itEquipmentOnlyItems = baseFilteredItems.filter(
        (row) => row.id && String(row.id).startsWith('it-')
    );

    // 3. Finally, segment active page viewport slice bounds
    const totalItemsCount = itEquipmentOnlyItems.length;
    const totalPages = Math.max(1, Math.ceil(totalItemsCount / itemsPerPage));
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginatedITItems = itEquipmentOnlyItems.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <div className="min-h-screen bg-slate-50/70 font-dynamic-title flex flex-col">
            <Header />

            <div className="max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 flex flex-col gap-6 flex-1">
                
                {/* Navigation Sub-bar */}
                <div className="bg-white border border-slate-200/80 shadow-xs rounded-xl px-4 py-2 flex items-center justify-between relative" ref={navRef}>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 border-r border-slate-200 pr-3 mr-1 hidden sm:inline">
                            Modules
                        </span>
                        
                        {menuItems.map((item) => {
                            const isDropdownOpen = activeDropdown === item.name;
                            return (
                                <div key={item.name} className="relative">
                                    <button
                                        onClick={() => setActiveDropdown(isDropdownOpen ? null : item.name)}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                                            isDropdownOpen 
                                            ? 'bg-[#002855] bg-opacity-80 text-white shadow-xs' 
                                            : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900'
                                        }`}
                                    >
                                        <span className={isDropdownOpen ? 'text-slate-300' : 'text-slate-400'}>
                                            {item.icon}
                                        </span>
                                        {item.name}
                                        <ChevronDown size={13} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180 text-slate-300' : 'text-slate-400'}`} />
                                    </button>

                                    {isDropdownOpen && (
                                        <div className="absolute left-0 mt-2 w-56 bg-white border border-slate-200/90 rounded-xl shadow-lg z-50 p-1.5">
                                            {item.children.map((child) => (
                                                <Link
                                                    key={child.name}
                                                    to={child.path}
                                                    className="flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg transition-all group"
                                                >
                                                    <span>{child.name}</span>
                                                    <ChevronRight size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-all transform translate-x-[-4px] group-hover:translate-x-0" />
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="hidden md:flex items-center gap-2 text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-100 rounded-lg px-2.5 py-1">
                        <span className={`w-1.5 h-1.5 rounded-full bg-emerald-500 ${loading ? 'animate-ping' : 'animate-pulse'}`} />
                        {loading ? "FETCHING DATA PIPELINES" : "SYSTEM NODE LOGS ACTIVE"}
                    </div>
                </div>

                {/* Page Title & Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-1">
                    <div>
                        <h1 className="text-lg font-bold text-slate-900 tracking-tight">IT Hardware Ledger</h1>
                        <p className="text-xs text-slate-500 mt-0.5">Track serial keys, categories, financial costs, and individual caretakers.</p>
                    </div>
                    
                    <div className="flex items-center gap-2.5 self-end sm:self-center">
                        <button className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg shadow-xs hover:bg-slate-50 transition-all cursor-pointer">
                            <Filter size={14} />
                            Filter View
                        </button>
                    </div>
                </div>

                {/* KPI Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-xl border border-slate-200/80 shadow-xs flex items-center justify-between">
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Tracked SKUs</span>
                            <span className="text-2xl font-bold text-slate-900 block">{metrics.totalSKUs}</span>
                        </div>
                        <div className="p-2.5 bg-slate-50 border border-slate-100 text-slate-700 rounded-lg"><Package size={18} /></div>
                    </div>

                    <div className="bg-white p-5 rounded-xl border border-slate-200/80 shadow-xs flex items-center justify-between">
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Low Stock Warning</span>
                            <span className="text-2xl font-bold text-amber-600 block">{metrics.lowStock}</span>
                        </div>
                        <div className="p-2.5 bg-amber-50/60 border border-amber-100/80 text-amber-600 rounded-lg"><AlertTriangle size={18} /></div>
                    </div>

                    <div className="bg-white p-5 rounded-xl border border-slate-200/80 shadow-xs flex items-center justify-between">
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Out of Stock</span>
                            <span className="text-2xl font-bold text-rose-600 block">{metrics.outOfStock}</span>
                        </div>
                        <div className="p-2.5 bg-rose-50/60 border border-rose-100/80 text-rose-600 rounded-lg"><XCircle size={18} /></div>
                    </div>

                    <div className="bg-white p-5 rounded-xl border border-slate-200/80 shadow-xs flex items-center justify-between">
                        <div className="space-y-1">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">IT Valuation Pool</span>
                            <span className="text-2xl font-bold text-slate-900 block">
                                ${metrics.valuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                        <div className="p-2.5 bg-emerald-50/60 border border-emerald-100/80 text-emerald-600 rounded-lg"><DollarSign size={18} /></div>
                    </div>
                </div>

                {/* Critical Restock Side Panel Block */}
                <div className="grid grid-cols-12 gap-5">
                    <div className="col-span-12 lg:col-span-7 bg-white p-5 rounded-xl border border-slate-200/80 shadow-xs space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Logistics Flow Dynamics</h3>
                                <p className="text-[10px] text-slate-400">Comparing item check-ins against depletion vectors.</p>
                            </div>
                            <span className="text-[10px] font-mono font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">Live Stream telemetry</span>
                        </div>
                        <div className="h-48 bg-slate-50/50 rounded-xl border border-dashed border-slate-200 flex items-center justify-center text-xs font-medium text-slate-400">
                            [Interactive Recharts Area Component Stream Node - Connected]
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-5 bg-white p-5 rounded-xl border border-slate-200/80 shadow-xs space-y-4">
                        <div>
                            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Critical Restock Triggers</h3>
                            <p className="text-[10px] text-slate-400">Items matching or dipping beneath safety parameters.</p>
                        </div>
                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                            {criticalTriggers.length === 0 ? (
                                <p className="text-center py-8 text-slate-400 text-xs font-medium">All item reserves pass stability baselines.</p>
                            ) : (
                                criticalTriggers.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2.5 border border-slate-100 rounded-lg bg-slate-50/50 text-xs hover:bg-slate-50 transition-colors">
                                        <div className="space-y-0.5 min-w-0 pr-2">
                                            <span className="font-semibold text-slate-700 block truncate">{item.name}</span>
                                            <span className="text-[10px] font-medium text-slate-400 block">Current Stock: {item.qty} / Safety Threshold: {item.min}</span>
                                        </div>
                                        <span className={`shrink-0 px-2 py-0.5 border text-[9px] font-bold uppercase tracking-wider rounded-md ${
                                            item.qty === 0 ? 'bg-rose-50 text-rose-700 border-rose-200/60' : 'bg-amber-50 text-amber-700 border-amber-200/60'
                                        }`}>
                                            {item.status}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Master Explorer Table Container */}
                <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden">
                    <div className="p-4 border-b border-slate-100 bg-slate-50/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="relative max-w-xs w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                            <input 
                                type="text" 
                                placeholder="Query by Model, SKU, Name, Location..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/5 focus:border-slate-400 outline-none transition-all"
                            />
                        </div>
                        <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide">
                            {loading ? "Syncing variables..." : `Showing ${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, totalItemsCount)} of ${totalItemsCount} IT Assets`}
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20 gap-2 text-slate-400">
                                <Loader2 size={24} className="animate-spin text-slate-700" />
                                <span className="text-xs font-medium">Compiling records matrix...</span>
                            </div>
                        ) : paginatedITItems.length === 0 ? (
                            <div className="text-center py-20 text-slate-400 text-xs font-medium">
                                No hardware items matched the active search parameters.
                            </div>
                        ) : (
                            <>
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr className="bg-slate-50/70 border-b border-slate-200/60 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                            <th className="p-3 pl-5">SKU / Serial No.</th>
                                            <th className="p-3">Item Description</th>
                                            <th className="p-3">Category Cluster</th>
                                            <th className="p-3">Stock Buffer</th>
                                            <th className="p-3">Assigned Location</th>
                                            <th className="p-3">Status</th>
                                            <th className="p-3 pr-5 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-slate-600 font-medium">
                                        {paginatedITItems.map((row) => {
                                            let badgeColor = "bg-emerald-50 text-emerald-700 border-emerald-200/60";
                                            if (row.status === "Functional") badgeColor = "bg-amber-50 text-amber-700 border-amber-200/60";
                                            if (row.status === "Good") badgeColor = "bg-rose-50 text-rose-700 border-rose-200/60";

                                            return (
                                                <tr key={row.id} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="p-3 pl-5 font-mono font-bold text-slate-400 text-[11px] truncate max-w-[140px]">{row.sku}</td>
                                                    <td className="p-3">
                                                        <div className="font-semibold text-slate-900">{row.name}</div>
                                                        <div className="text-[10px] text-slate-400 mt-0.5 font-medium">{row.meta}</div>
                                                    </td>
                                                    <td className="p-3 text-slate-500">{row.category}</td>
                                                    <td className="p-3 font-medium text-slate-700">{row.stock} unit{row.stock !== 1 && 's'}</td>
                                                    <td className="p-3 text-slate-500 font-medium">{row.location}</td>
                                                    <td className="p-3">
                                                        <span className={`inline-flex px-2 py-0.5 border text-[9px] font-bold uppercase tracking-wider rounded-md ${badgeColor}`}>
                                                            {row.status}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 pr-5 text-right">
                                                        <button className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer p-1 rounded-md hover:bg-slate-100">
                                                            <MoreHorizontal size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* PAGINATION CONTROLS BAR */}
                                <div className="p-4 border-t border-slate-100 bg-slate-50/20 flex items-center justify-between text-xs font-semibold text-slate-500">
                                    <div>
                                        Page <span className="text-slate-800">{currentPage}</span> of <span className="text-slate-800">{totalPages}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button 
                                            onClick={() => setCurrentPage(1)} 
                                            disabled={currentPage === 1}
                                            className="p-1.5 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-slate-600 disabled:opacity-40 disabled:hover:bg-white transition-all cursor-pointer disabled:cursor-not-allowed"
                                        >
                                            <ChevronsLeft size={14} />
                                        </button>
                                        <button 
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                            disabled={currentPage === 1}
                                            className="p-1.5 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-slate-600 disabled:opacity-40 disabled:hover:bg-white transition-all cursor-pointer disabled:cursor-not-allowed flex items-center gap-1 px-2.5"
                                        >
                                            <ChevronLeft size={14} />
                                            Prev
                                        </button>
                                        <button 
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                            disabled={currentPage === totalPages}
                                            className="p-1.5 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-slate-600 disabled:opacity-40 disabled:hover:bg-white transition-all cursor-pointer disabled:cursor-not-allowed flex items-center gap-1 px-2.5"
                                        >
                                            Next
                                            <ChevronRight size={14} />
                                        </button>
                                        <button 
                                            onClick={() => setCurrentPage(totalPages)} 
                                            disabled={currentPage === totalPages}
                                            className="p-1.5 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-slate-600 disabled:opacity-40 disabled:hover:bg-white transition-all cursor-pointer disabled:cursor-not-allowed"
                                        >
                                            <ChevronsRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}